import actions from './actions';
import types from './constants';
import SRS from '../../../../services/StudentRecordService';
import { hasPendingAssessment } from '../../util/pendingAssessment';
import { generateSubscores } from '../../util/generators/generateSubscores';
import { fetchCurrentPeriod } from '../../../../services/query/calendarAPI';
import get from 'lodash.get';
import { getUserData } from 'Services/API';
import { getMostCommonGrade } from '../../../../utilities/getTimeAssignedByGrade';
import { getSchoolYearWithFallbacks } from '../../../../utilities/getSchoolYearWithFallbacks';
import store from '../../../../store';

// Helper function to wait for Redux state condition
const waitForState = (selector, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    // Check if condition is already met
    if (selector(store.getState())) {
      return resolve(store.getState());
    }
    
    // Set up subscription
    const unsubscribe = store.subscribe(() => {
      if (selector(store.getState())) {
        unsubscribe();
        resolve(store.getState());
      } else if (Date.now() - startTime > timeout) {
        unsubscribe();
        reject(new Error('Timeout waiting for state condition'));
      }
    });
  });
};

const fetchReport = (classId, student, selectedPercentiles) => async (dispatch, getState) => {
  let studentId = null
  let grade = null
  if (student) studentId = student.value
  if (student) grade = student.grade

  dispatch(actions[types.READINGROPE_PENDING]())

  try {
    // Wait for required state to be loaded before proceeding
    await waitForState(state => {
      const screeningWindow = get(state, 'reports.teacher.selectedScreeningWindow');
      const districtId = get(state, 'userData.district.id');
      const schoolId = get(state, 'reports.roster.ui.selections.school.id');
      
      // For English, we need selectedScreeningWindow with id, startDate, endDate
      // For Spanish, we just need districtId and schoolId (we'll fetch current period)
      const locale = get(state, 'reports.teacher.selectedLocale');
      const formattedLocale = locale === "EN-US" ? "EN_US" : locale === "ES-MX" ? "ES_MX" : locale;
      
      if (formattedLocale === "ES_MX") {
        // Spanish: need districtId and schoolId to fetch current period
        return districtId && schoolId;
      } else {
        // English: need screening window with proper data
        return screeningWindow && screeningWindow.id && screeningWindow.startDate && screeningWindow.endDate && districtId && schoolId;
      }
    });

    const state = getState();
    const locale = get(state, 'reports.teacher.selectedLocale');
    const selectedAssessmentTypeFilter = get(state, 'reports.teacher.selectedAssessmentTypeFilter');
    const useARM = selectedPercentiles && selectedPercentiles.state !== "US";
    const {useLatestARM} = getUserData();
    
    // Convert locale format from "EN-US"/"ES-MX" to "EN_US"/"ES_MX" for generateSubscores
    const formattedLocale = locale === "EN-US" ? "EN_US" : locale === "ES-MX" ? "ES_MX" : locale;
    
    // Get additional parameters needed for generateSubscores
    const window = get(state, 'reports.teacher.selectedScreeningWindow', {});
    const districtId = get(state, 'userData.district.id');
    const schoolId = get(state, 'reports.roster.ui.selections.school.id');
    
    // Use school year dates instead of testing window dates
    const assessmentCalendar = state.calendar?.assessmentCalendar;
    const schoolYearCalendar = state.calendar?.schoolYearCalendar;
    const schoolYearResult = getSchoolYearWithFallbacks(assessmentCalendar, schoolYearCalendar);
    let startDate = schoolYearResult.startDate;
    let endDate = schoolYearResult.endDate;

    let predominantGrade = grade;
    let studentIds = studentId ? [studentId] : null;

    if (!studentId) {
      // Whole class selected - get all students in class and find predominant grade
      const students = get(state, 'reports.roster.data.student', []);
      const classStudents = students.filter(s => s.classroom === classId);

      if (classStudents && classStudents.length > 0) {
        // Get all student IDs that have data (filter out students without data)
        studentIds = classStudents.map(s => s.id).filter(id => id);

        // Find predominant grade using proper utility that counts frequency
        predominantGrade = getMostCommonGrade(classStudents) || 1; // Default to grade 1 if no grades found

      } else {
        console.warn('No students found for class', classId);
        // Return error if no students found
        dispatch(actions[types.READINGROPE_ERROR]('No students found for the selected class'));
        return;
      }
    }
    
    // Determine period and screeningWindow based on locale and selected window:
    // - Spanish: Use current period from calendaring service
    // - English: Use selected period from UI, and extract screeningWindow for benchmark windows
    let period = get(state, 'reports.teacher.selectedScreeningWindow.id');
    let screeningWindow = null;
    
    if (formattedLocale === "ES_MX") {
      try {
        const currentPeriodResponse = await fetchCurrentPeriod({ schoolId: schoolId.toString() });
        const currentPeriod = currentPeriodResponse?.getCurrentPeriod?.periodIdentifier;
        if (currentPeriod) {
          period = currentPeriod;
          // Keep school year dates, only update the period
        } else {
          console.warn("Could not get current period for Spanish, falling back to period selector value:", period);
        }
      } catch (error) {
        console.error("Error fetching current period for Spanish:", error);
        console.warn("Falling back to period selector value:", period);
      }
    } else {
      // For English, check if the selected window is a benchmark window
      const selectedWindow = get(state, 'reports.teacher.selectedScreeningWindow');
      if (selectedWindow && selectedWindow.screeningWindow) {
        // This is a benchmark window (BOY, MOY, EOY)
        screeningWindow = selectedWindow.screeningWindow;
      }
    }

    let requests = [
      // Keep getting readingRope data for skill recommendations
      SRS.getReadingRopeData({ classId, studentId, useARM, locale, useLatestARM }),
      // Add subscores using generateSubscores function with formatted locale
      // For whole class, pass studentIds array and predominant grade
      generateSubscores(classId, studentId, studentIds, useARM, formattedLocale, useLatestARM, startDate, endDate, districtId, schoolId, predominantGrade, period, screeningWindow, selectedAssessmentTypeFilter)
    ]
    if(studentId){
      requests.push(hasPendingAssessment(studentId))
    }
    const [readingRopeData, subscores, isPending] = await Promise.all(requests);
    
    // Combine the data
    const combinedData = {
      ...readingRopeData,
      subscores,
      isPending: isPending || false
    };

    dispatch(actions[types.READINGROPE_COMPLETE](combinedData));
  }
  catch (e) {
    console.log("ERROR:",e)
    dispatch(actions[types.READINGROPE_ERROR](e))
  }
};

export default {
  fetchReport,
}